import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  TextField,
  Button,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SyncIcon from '@mui/icons-material/Sync';
import {
  getAllAppointmentTypes,
  getClinicAppointmentTypes,
  updateClinicAppointmentTypes,
  syncAppointmentTypes
} from '../services/adminService';
import axios from 'axios';

const AppointmentTypeManager = ({ clinicId }) => {
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  useEffect(() => {
    fetchCategories();
    if (clinicId) {
      fetchAppointmentTypes();
    }
  }, [clinicId]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/admin/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };
  
  const fetchAppointmentTypes = async () => {
    try {
      setLoading(true);
      const data = await getClinicAppointmentTypes(clinicId);
      setAppointmentTypes(data);
    } catch (error) {
      setError('Failed to load appointment types');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSync = async () => {
    try {
      setSyncLoading(true);
      const result = await syncAppointmentTypes();
      setSuccess(result.message || 'Successfully synced appointment types from Cliniko');
      
      // Reload appointment types after sync
      await fetchAppointmentTypes();
    } catch (error) {
      setError('Failed to sync appointment types');
      console.error(error);
    } finally {
      setSyncLoading(false);
    }
  };
  
  const handleEnabledChange = (id) => (event) => {
    const updatedTypes = appointmentTypes.map(type => {
      if (type.id === id) {
        return { ...type, is_enabled: event.target.checked };
      }
      return type;
    });
    setAppointmentTypes(updatedTypes);
  };
  
  const handlePriceChange = (id) => (event) => {
    const price = event.target.value === '' ? null : parseFloat(event.target.value);
    
    const updatedTypes = appointmentTypes.map(type => {
      if (type.id === id) {
        return { ...type, price };
      }
      return type;
    });
    setAppointmentTypes(updatedTypes);
  };

  const handleCategoryChange = (id) => (event) => {
    const category_id = event.target.value === '' ? null : parseInt(event.target.value);
    
    const updatedTypes = appointmentTypes.map(type => {
      if (type.id === id) {
        return { ...type, category_id };
      }
      return type;
    });
    setAppointmentTypes(updatedTypes);
  };
  
  const moveItem = (index, direction) => {
    const newItems = [...appointmentTypes];
    const item = newItems[index];
    
    if (direction === 'up' && index > 0) {
      newItems[index] = newItems[index - 1];
      newItems[index - 1] = item;
    } else if (direction === 'down' && index < newItems.length - 1) {
      newItems[index] = newItems[index + 1];
      newItems[index + 1] = item;
    }
    
    // Update sort order
    const sortedItems = newItems.map((item, idx) => ({
      ...item,
      sort_order: idx * 10
    }));
    
    setAppointmentTypes(sortedItems);
  };
  
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(appointmentTypes);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update sort_order based on new position
    const updatedItems = items.map((item, index) => ({
      ...item,
      sort_order: index * 10
    }));
    
    setAppointmentTypes(updatedItems);
  };
  
  const handleSaveChanges = async () => {
    try {
      setLoading(true);
      
      // Prepare data for API
      const appointmentTypeSettings = appointmentTypes.map(type => ({
        id: type.id,
        is_enabled: type.is_enabled,
        custom_price: type.price, // This will be sent as custom_price
        sort_order: type.sort_order,
        category_id: type.category_id
      }));
      
      console.log('Saving appointment types:', appointmentTypeSettings);
      await updateClinicAppointmentTypes(clinicId, appointmentTypeSettings);
      setSuccess('Appointment types updated successfully');
    } catch (error) {
      setError('Failed to update appointment types: ' + (error.response?.data?.message || error.message));
      console.error('Save error details:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCloseSnackbar = () => {
    setError(null);
    setSuccess(null);
  };
  
  if (!clinicId) {
    return <Alert severity="warning">Please select a clinic first.</Alert>;
  }
  
  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item>
          <Typography variant="h6">Appointment Types</Typography>
        </Grid>
        <Grid item>
          <Button 
            variant="outlined" 
            startIcon={<SyncIcon />}
            onClick={handleSync}
            disabled={syncLoading}
          >
            {syncLoading ? 'Syncing...' : 'Sync from Cliniko'}
          </Button>
        </Grid>
      </Grid>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="appointment-types">
              {(provided) => (
                <TableContainer component={Paper} ref={provided.innerRef} {...provided.droppableProps}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Price ($)</TableCell>
                        <TableCell>Enabled</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {appointmentTypes.map((type, index) => (
                        <Draggable key={type.id} draggableId={type.id.toString()} index={index}>
                          {(provided) => (
                            <TableRow
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              hover
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => moveItem(index, 'up')}
                                    disabled={index === 0}
                                  >
                                    <ArrowUpwardIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => moveItem(index, 'down')}
                                    disabled={index === appointmentTypes.length - 1}
                                  >
                                    <ArrowDownwardIcon fontSize="small" />
                                  </IconButton>
                                  {type.sort_order / 10 + 1}
                                </Box>
                              </TableCell>
                              <TableCell>{type.name}</TableCell>
                              <TableCell>{type.duration_in_minutes} mins</TableCell>
                              <TableCell>
                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                  <Select
                                    value={type.category_id || ''}
                                    onChange={handleCategoryChange(type.id)}
                                    displayEmpty
                                  >
                                    <MenuItem value="">
                                      <em>No Category</em>
                                    </MenuItem>
                                    {categories.map((category) => (
                                      <MenuItem key={category.id} value={category.id}>
                                        {category.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  type="number"
                                  value={type.price !== null && type.price !== undefined ? type.price : ''}
                                  onChange={handlePriceChange(type.id)}
                                  variant="outlined"
                                  size="small"
                                  InputProps={{
                                    inputProps: { 
                                      min: 0,
                                      step: 0.01
                                    }
                                  }}
                                  sx={{ width: '80px' }}
                                />
                              </TableCell>
                              <TableCell>
                                <Switch
                                  checked={type.is_enabled}
                                  onChange={handleEnabledChange(type.id)}
                                  color="primary"
                                />
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Droppable>
          </DragDropContext>
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="contained" 
              onClick={handleSaveChanges}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </>
      )}
      
      <Snackbar 
        open={!!error || !!success} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={error ? 'error' : 'success'} 
          sx={{ width: '100%' }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AppointmentTypeManager;